// src/context/AuthContext.js
import React, { createContext, useState, useContext, useEffect } from 'react';
import axios from 'axios';

// Créer le contexte
const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isInitialized, setIsInitialized] = useState(false);

  // Configurer l'intercepteur axios pour inclure le token dans chaque requête
  useEffect(() => {
    const initAuth = async () => {
      setLoading(true);
      const token = localStorage.getItem('token');

      if (token) {
        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        await checkAuth();
      } else {
        setLoading(false);
      }

      setIsInitialized(true);
    };

    initAuth();
  }, []);

  // Vérifier si l'utilisateur est déjà connecté
  // Dans AuthContext.js, modifier la fonction checkAuth :

  const checkAuth = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      setLoading(false);
      setUser(null);
      return;
    }

    try {
      // Définir explicitement l'en-tête Authorization
      const response = await axios.get(`https://api.greenecobagsusa.com/api/me`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        }
      });

      if (response.data.success) {
        setUser(response.data.user);
      } else {
        localStorage.removeItem('token');
        delete axios.defaults.headers.common['Authorization'];
        setUser(null);
      }
    } catch (error) {
      console.error("Erreur de vérification d'authentification:", error);

      // Si l'erreur est 401, le token est invalide
      if (error.response?.status === 401) {
        localStorage.removeItem('token');
        delete axios.defaults.headers.common['Authorization'];
        setUser(null);
      }
    } finally {
      setLoading(false);
    }
  };

  // Modifier aussi la fonction login pour s'assurer que le token est bien configuré :
  const login = async (email, password) => {
    setError(null);
    setLoading(true);

    try {
      const response = await axios.post(`https://api.greenecobagsusa.com/api/login`, {
        email,
        password
      });

      if (response.data.token) {
        const { token, user } = response.data;
        localStorage.setItem('token', token);

        // Configurer axios pour toutes les requêtes futures
        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;

        setUser(user);
        console.log('Utilisateur connecté avec succès:', user);
        setLoading(false);
        return { success: true, user };
      } else {
        setLoading(false);
        return {
          success: false,
          message: response.data.message,
          errors: response.data.errors
        };
      }
    } catch (error) {
      console.error("Erreur de connexion:", error);
      setLoading(false);

      if (error.response) {
        return {
          success: false,
          message: error.response.data.message || "Erreur de connexion",
          errors: error.response.data.errors
        };
      } else {
        return {
          success: false,
          message: "Impossible de se connecter au serveur"
        };
      }
    }
  };
  // Fonction de déconnexion
  const logout = async () => {
    setLoading(true);
    try {
      // Corriger l'URL de l'API (il manque un "s" dans https)
      await axios.post(`https://api.greenecobagsusa.com/api/logout`);
    } catch (error) {
      console.error("Erreur lors de la déconnexion:", error);
    } finally {
      // Même en cas d'erreur, on nettoie la session côté client
      localStorage.removeItem('token');
      delete axios.defaults.headers.common['Authorization'];
      setUser(null);
      setLoading(false);
    }
  };

  // Valeurs et fonctions exposées par le contexte
  const value = {
    user,
    loading,
    error,
    login,
    logout,
    isAuthenticated: !!user,
    isInitialized
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

// Hook personnalisé pour utiliser le contexte d'authentification
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth doit être utilisé avec AuthProvider');
  }
  return context;
};

export default AuthContext;